import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Parallax } from "react-parallax";
import { matchPath, useLocation, useParams } from "react-router-dom";

import { CrossIconWhiteLarge } from "../../assets";
import { propertyConstants } from "../../constants";
import { EMPTY_STRING, SCREEN_PATH } from "../../constants/global.constants";
import {
  hasUserAccess,
  convertCurrency,
  formatToTwoDecimalPlaces,
} from "../../shared/utility";
import MainSpinner from "../../shared/components/spinner/MainSpinner";
import MainContentWrapper from "../../shared/components/main-content-wrapper/MainContentWrapper";
import { propertyActions } from "../../redux/actions";
import ImageSlider from "./ImageSlider";
import Topbar from "../../layout/Topbar";
import AgentDetails from "./AgentDetails";
import ExpandedSlider from "./ExpandedSlider";
import NearbyLocations from "./NearbyLocations";
import FeaturesSection from "./PropertyFeatures";
import PropertyDescription from "./PropertyDescription";

const { GET_PROPERTY_DETAIL_BY_ID } = propertyConstants;

const PropertyDetailPage = (props) => {
  const {
    propertyDetail,
    allCurrencies,
    isFromFavourites,
    makePropertyFavourite,
    addToNotInterestedProperty,
    removePropertyFromFavourite,
    removeFromNotInterestedProperty,
    spinnerActions,
    getPropertyDetailsForUser,
    defaultCurrency,
    user: { subscriptionStatus },
    userRole,
  } = props;

  const { propertyId } = useParams();
  const { pathname } = useLocation();
  const isDetailsPage = !!matchPath(
    SCREEN_PATH.ADMIN_PROPERTY_DETAILS,
    pathname
  );
  const [isFavourite, setIsFavourite] = useState(false);
  const [isInterested, setIsInterested] = useState(false);
  const [slideCurrentIndex, setSlideCurrentIndex] = useState(0);
  const [expandSlider, setExpandSlider] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [, setLoadedImagesVersion] = useState(0);
  const loadedFullUrlsRef = useRef(new Set());

  const hasUserRole = hasUserAccess(userRole);

  const defaultCurrencyObject = allCurrencies.find(
    (currency) => currency.id === Number(defaultCurrency)
  );

  const getConvertedValue = (price) =>
    convertCurrency(
      price,
      propertyDetail?.currency?.code,
      defaultCurrencyObject,
      allCurrencies
    );

  const isFavouriteHandle = () => {
    if (!isFavourite) {
      makePropertyFavourite(propertyId);
    } else {
      removePropertyFromFavourite(propertyId);
    }
    setIsFavourite((prev) => !prev);
  };

  const isInterestedHandle = () => {
    if (!isInterested) {
      addToNotInterestedProperty(propertyId);
    } else {
      removeFromNotInterestedProperty(propertyId);
    }
    setIsInterested((prev) => !prev);
  };

  const markFullImageLoaded = useCallback((url) => {
    if (!url || loadedFullUrlsRef.current.has(url)) {
      return;
    }
    loadedFullUrlsRef.current.add(url);
    setLoadedImagesVersion((version) => version + 1);
  }, []);

  const preloadFullImage = useCallback(
    (url) => {
      if (!url || loadedFullUrlsRef.current.has(url)) {
        return;
      }
      const img = new Image();
      img.onload = () => markFullImageLoaded(url);
      img.onerror = () => markFullImageLoaded(url);
      img.src = url;
    },
    [markFullImageLoaded]
  );

  const getBackgroundImage = () => {
    const image = propertyDetail?.images?.[slideCurrentIndex];
    if (!image) {
      return EMPTY_STRING;
    }
    if (loadedFullUrlsRef.current.has(image.url)) {
      return image.url;
    }
    return image.thumbnailUrl ?? image.url;
  };

  useEffect(() => {
    loadedFullUrlsRef.current = new Set();
    setSlideCurrentIndex(0);
    setLoadedImagesVersion((version) => version + 1);
    getPropertyDetailsForUser(propertyId);
    // eslint-disable-next-line
  }, [propertyId]);

  useEffect(() => {
    const images = propertyDetail?.images;
    if (!images?.length) {
      return;
    }

    const length = images.length;
    const neighborIndexes = [
      slideCurrentIndex,
      (slideCurrentIndex + 1) % length,
      (slideCurrentIndex - 1 + length) % length,
    ];

    neighborIndexes.forEach((index) => {
      preloadFullImage(images[index]?.url);
    });
  }, [slideCurrentIndex, propertyDetail?.images, preloadFullImage]);

  useEffect(() => {
    setIsFavourite(!!propertyDetail?.isFavourite);
    setIsInterested(!!propertyDetail?.isNotInterested);

    // eslint-disable-next-line
  }, [propertyDetail]);

  const spinner = useCallback(
    (type) => {
      return spinnerActions.findIndex((s) => s === type);
    },
    // eslint-disable-next-line
    [spinnerActions]
  );

  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY > 0;
    setIsScrolled((prev) => (prev !== scrolled ? scrolled : prev));
  }, []);

  useEffect(() => {
    // Attach scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Run it once on mount in case user is already scrolled
    handleScroll();

    // Cleanup
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {spinner(GET_PROPERTY_DETAIL_BY_ID) > -1 && <MainSpinner />}

      {!isDetailsPage && <Topbar />}
      <MainContentWrapper>
        {expandSlider && (
          <div className="images-expanded-slider">
            <div className="position-relative h-100 d-flex align-items-center px-xs-1 px-3">
              <ExpandedSlider images={propertyDetail?.images ?? []} />
              <img
                src={CrossIconWhiteLarge}
                className="slider-cross-icon"
                onClick={() => setExpandSlider(false)}
              />
            </div>
          </div>
        )}
        <Parallax
          bgImage={getBackgroundImage()}
          strength={0}
          bgClassName="parallex"
          className={isScrolled ? "scrolled" : ""}
        >
          <div className="container p-0">
            <ImageSlider
              property={propertyDetail}
              isFavourite={isFavourite}
              isInterested={isInterested}
              isFavouriteHandle={isFavouriteHandle}
              isInterestedHandle={isInterestedHandle}
              slideCurrentIndex={slideCurrentIndex}
              setSlideCurrentIndex={setSlideCurrentIndex}
              isFromFavourites={isFromFavourites}
              getConvertedValue={getConvertedValue}
              currencySymbol={defaultCurrencyObject?.symbol}
              propertyDetail={propertyDetail}
              hasUserRole={hasUserRole}
              expandSlider={() => setExpandSlider(true)}
            />
          </div>
          <div className="container parallex-content">
            <FeaturesSection
              features={propertyDetail?.features ?? []}
              amenities={propertyDetail?.amenity ?? {}}
            />
            {/* Nearby locations section */}
            {propertyDetail?.nearbyLocations?.length > 0 && (
              <NearbyLocations locations={propertyDetail?.nearbyLocations} />
            )}
            {/* Property description section */}
            <PropertyDescription
              description={propertyDetail?.description}
              depositRequired={
                hasUserRole
                  ? getConvertedValue(propertyDetail?.depositAmount)
                  : formatToTwoDecimalPlaces(propertyDetail?.depositAmount)
              }
              currency={propertyDetail?.currency}
              listingType={propertyDetail?.listingType}
              availability={propertyDetail?.availability}
              currencyCode={
                hasUserRole
                  ? defaultCurrencyObject?.code
                  : propertyDetail?.currency?.code
              }
              property={propertyDetail}
            />
            {/* Agent details section */}
            <AgentDetails
              hasUserRole={hasUserRole}
              agent={propertyDetail?.agent}
              location={propertyDetail?.location}
              subscriptionStatus={subscriptionStatus}
              refetchDetails={() => getPropertyDetailsForUser(propertyId)}
            />
          </div>
        </Parallax>
      </MainContentWrapper>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.authReducer.user,
  userRole: state.authReducer.userRole,
  allCurrencies: state.homeReducer.allCurrencies,
  defaultCurrency: state.homeReducer.defaultCurrency,
  spinnerActions: state.systemReducer.spinnerActions,
  propertyDetail: state.propertyReducer.propertyDetail,
});

const mapDispatchToProps = (dispatch) => ({
  getPropertyDetailById: (propertyId) =>
    dispatch(propertyActions.getPropertyDetailById(propertyId)),
  makePropertyFavourite: (body) =>
    dispatch(propertyActions.makePropertyFavourite(body)),
  addToNotInterestedProperty: (body) =>
    dispatch(propertyActions.addToNotInterestedProperty(body)),
  removePropertyFromFavourite: (body) =>
    dispatch(propertyActions.removePropertyFromFavourite(body)),
  removeFromNotInterestedProperty: (body) =>
    dispatch(propertyActions.removeFromNotInterestedProperty(body)),
  getPropertyDetailsForUser: (id) =>
    dispatch(propertyActions.getPropertyDetailsForUser(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PropertyDetailPage);
